import * as React from "react";

import useEmblaCarousel from "embla-carousel-react";
import { Button } from "@mui/material";
import { carouselStyles, carouselSxStyles } from "./styles";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";

const CarouselContext = React.createContext(null);

function useCarousel() {
  const context = React.useContext(CarouselContext);
  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />");
  }
  return context;
}

const Carousel = React.forwardRef(
  (
    {
      orientation = "horizontal",
      opts,
      setApi,
      plugins,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const classes = carouselStyles();
    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        axis: orientation === "horizontal" ? "x" : "y",
      },
      plugins
    );
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);

    const onSelect = React.useCallback((api) => {
      if (!api) return;
      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    }, []);

    const scrollPrev = React.useCallback(() => {
      if (api !== undefined) api.scrollPrev();
    }, [api]);

    const scrollNext = React.useCallback(() => {
      if (api !== undefined) api.scrollNext();
    }, [api]);

    const handleKeyDown = React.useCallback(
      (event) => {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          scrollPrev();
        } else if (event.key === "ArrowRight") {
          event.preventDefault();
          scrollNext();
        }
      },
      [scrollPrev, scrollNext]
    );

    React.useEffect(() => {
      if (!api || !setApi) return;
      setApi(api);
    }, [api, setApi]);

    React.useEffect(() => {
      if (!api) return;
      onSelect(api);
      api.on("reInit", onSelect);
      api.on("select", onSelect);
      return () => api?.off("select", onSelect);
    }, [api, onSelect]);

    return (
      <CarouselContext.Provider
        value={{
          carouselRef,
          api,
          opts,
          orientation:
            orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
          scrollPrev,
          scrollNext,
          canScrollPrev,
          canScrollNext,
        }}
      >
        <div
          ref={ref}
          onKeyDownCapture={handleKeyDown}
          className={`${classes.carouselContainer} ${className}`}
          role="region"
          aria-roledescription="carousel"
          {...props}
        >
          {children}
        </div>
      </CarouselContext.Provider>
    );
  }
);
Carousel.displayName = "Carousel";

const CarouselContent = React.forwardRef(({ className, ...props }, ref) => {
  const classes = carouselStyles();
  const { carouselRef, orientation } = useCarousel();

  return (
    <div ref={carouselRef} className={classes.overflowHidden}>
      <div
        ref={ref}
        className={`${classes.flexContainer} ${
          orientation === "horizontal"
            ? classes.horizontalSpacing
            : classes.verticalSpacing
        } ${className}`}
        {...props}
      />
    </div>
  );
});
CarouselContent.displayName = "CarouselContent";

const CarouselItem = React.forwardRef(
  ({ className, isSelected = false, index = null, ...props }, ref) => {
    const classes = carouselStyles();
    const { orientation, api } = useCarousel();

    React.useEffect(() => {
      const scrollToView = () => {
        if (api.slidesNotInView().includes(index)) {
          api.scrollTo(index);
          api.off("slidesInView", scrollToView);
        }
      };
      if (index !== null && isSelected && api) {
        api.on("slidesInView", scrollToView);
      }

      return () => {
        if (api) {
          api.off("slidesInView", scrollToView);
        }
      };
    }, [api, index, isSelected]);

    return (
      <div
        ref={ref}
        role="group"
        aria-roledescription="slide"
        className={`${classes.carouselItem} ${
          orientation === "horizontal"
            ? classes.horizontalItemSpacing
            : classes.verticalItemSpacing
        } ${className}`}
        {...props}
      />
    );
  }
);
CarouselItem.displayName = "CarouselItem";

const CarouselPrevious = React.forwardRef(
  (
    {
      className,
      variant = "text",
      size = "small",
      children,
      renderButton,
      hideButton = false,
      removeBaseStyles = false,
      autoHideBtn = true,
      sxStyles,
      ...props
    },
    ref
  ) => {
    const classes = carouselStyles();
    const { orientation, scrollPrev, canScrollPrev, canScrollNext } =
      useCarousel();

    if (
      hideButton ||
      (autoHideBtn && !canScrollPrev) ||
      (!canScrollPrev && !canScrollNext)
    )
      return null;

    const defaultButton = (
      <Button
        ref={ref}
        variant={variant}
        size={size}
        id="carousel-prev-button"
        aria-label="Previous Slide"
        sx={{
          ...(removeBaseStyles
            ? {}
            : {
                ...carouselSxStyles.baseStyles,
                ...(orientation === "horizontal"
                  ? carouselSxStyles.prevButtonHorizontal
                  : carouselSxStyles.prevButtonVertical),
              }),
          ...carouselSxStyles.buttonBase,
          ...sxStyles,
        }}
        className={className}
        disabled={!canScrollPrev}
        onClick={scrollPrev}
        {...props}
      >
        {children || (
          <div className={classes.carouselBtnIconWrapper}>
            <ChevronLeftIcon
              style={{
                color: "#5B8989",
                transform:
                  orientation === "horizontal"
                    ? "rotate(0deg)"
                    : "rotate(90deg)",
              }}
            />
          </div>
        )}
      </Button>
    );

    return renderButton
      ? renderButton({ scrollPrev, canScrollPrev, ref, ...props })
      : defaultButton;
  }
);
CarouselPrevious.displayName = "CarouselPrevious";

const CarouselNext = React.forwardRef(
  (
    {
      className,
      variant = "text",
      size = "small",
      children,
      renderButton,
      hideButton = false,
      removeBaseStyles = false,
      autoHideBtn = true,
      sxStyles,
      ...props
    },
    ref
  ) => {
    const classes = carouselStyles();
    const { orientation, scrollNext, canScrollNext, canScrollPrev } =
      useCarousel();

    if (
      hideButton ||
      (autoHideBtn && !canScrollNext) ||
      (!canScrollPrev && !canScrollNext)
    )
      return null;

    const defaultButton = (
      <Button
        ref={ref}
        variant={variant}
        size={size}
        sx={{
          ...(removeBaseStyles
            ? {}
            : {
                ...carouselSxStyles.baseStyles,
                ...(orientation === "horizontal"
                  ? carouselSxStyles.nextButtonHorizontal
                  : carouselSxStyles.nextButtonVertical),
              }),

          ...carouselSxStyles.buttonBase,
          ...sxStyles,
        }}
        className={className}
        disabled={!canScrollNext}
        onClick={scrollNext}
        {...props}
      >
        {children || (
          <div className={classes.carouselBtnIconWrapper}>
            <ChevronLeftIcon
              style={{
                color: "#5B8989",
                transform:
                  orientation === "horizontal"
                    ? "rotate(180deg)"
                    : "rotate(-90deg)",
              }}
            />
          </div>
        )}
      </Button>
    );

    return renderButton
      ? renderButton({ scrollNext, canScrollNext, ref, ...props })
      : defaultButton;
  }
);
CarouselNext.displayName = "CarouselNext";

export {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
};
